import { useEffect, useState, useCallback } from "react";
import { BarChart3, TrendingUp, Layers, HeartPulse, Users, Settings, Save } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const PERIODS = [["today", "Today"], ["7d", "7 Days"], ["30d", "30 Days"], ["90d", "90 Days"], ["year", "1 Year"]];
const TABS = [["overview", "Marketplace", BarChart3], ["categories", "Categories", Layers], ["health", "Health", HeartPulse], ["sellers", "Sellers", Users], ["config", "Config", Settings]];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function Card({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [cfg, setCfg] = useState(null);

  const EP = { overview: "admin/overview", categories: "admin/categories", health: "admin/health", sellers: "admin/sellers" };

  const load = useCallback((t, p) => {
    if (t === "config") { api.get("/marketplace/analytics/admin/config").then((r) => setCfg(r.data.config)).catch(() => {}); return; }
    api.get(`/marketplace/analytics/${EP[t]}?period=${p}`).then((r) => setData((d) => ({ ...d, [t]: r.data }))).catch(() => setData((d) => ({ ...d, [t]: null })));
  }, []);
  useEffect(() => { load(tab, period); }, [tab, period, load]);

  const saveCfg = async () => {
    try { const r = await api.put("/marketplace/analytics/admin/config", { health_weights: cfg.health_weights, thresholds: cfg.thresholds, reason: "Admin update via console" }); setCfg(r.data.config); toast.success("Analytics config saved (audited)"); }
    catch { toast.error("Could not save config"); }
  };

  const o = data.overview?.overview; const cats = data.categories; const health = data.health?.health; const sellers = data.sellers?.sellers;

  return (
    <div data-testid="admin-analytics">
      <PageHeader title="Marketplace Analytics" icon={BarChart3} subtitle="Marketplace-wide business intelligence from finalized records. All financial metrics are authoritative." />

      <div className="mb-4 flex flex-wrap gap-2">
        {PERIODS.map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} data-testid={`aperiod-${v}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${period === v ? "brand-gradient-bg text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{l}</button>)}
      </div>
      <div className="mb-5 flex flex-wrap gap-1.5 border-b border-slate-200 pb-2" data-testid="admin-analytics-tabs">
        {TABS.map(([v, l, Icon]) => <button key={v} onClick={() => setTab(v)} data-testid={`atab-${v}`} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Icon className="h-3.5 w-3.5" /> {l}</button>)}
      </div>

      {tab === "overview" && o && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Card testid="mk-gmv" label="GMV" value={inr(o.gmv)} />
            <Card testid="mk-orders" label="Orders" value={o.orders} />
            <Card testid="mk-aov" label="AOV" value={inr(o.aov)} />
            <Card testid="mk-units" label="Units" value={o.units} />
            <Card testid="mk-sellers" label="Active Sellers" value={o.active_sellers} />
            <Card testid="mk-customers" label="Customers" value={o.customers} />
            <Card testid="mk-products" label="Active Products" value={o.active_products} />
            <Card testid="mk-commission" label="Commission" value={inr(o.commission)} />
            <Card testid="mk-returns" label="Returns" value={o.returns} />
            <Card testid="mk-refunds" label="Refunds" value={o.refunds} />
          </div>
          <p className="text-xs text-slate-400">{o.note}</p>
        </div>
      )}

      {tab === "categories" && cats && (
        <div className="space-y-4">
          <Table testid="admin-cat-table" head={["Category", "Revenue", "Units", "Products", "Growth"]}
            rows={(cats.categories || []).map((c) => [c.name, inr(c.revenue), c.units, c.product_count, c.growth_pct != null ? `${c.growth_pct}%` : "—"])} empty="No category sales in this period." />
          <div className="grid gap-3 sm:grid-cols-2">
            <MiniList testid="growing-cats" title="Growing Categories" items={cats.growing} render={(x) => `${x.name} — ▲ ${x.growth_pct}%`} />
            <MiniList testid="declining-cats" title="Declining Categories" items={cats.declining} render={(x) => `${x.name} — ▼ ${Math.abs(x.growth_pct)}%`} />
          </div>
        </div>
      )}

      {tab === "health" && health && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <Card testid="h-fulfil" label="Fulfilment Rate" value={health.fulfilment_rate != null ? `${health.fulfilment_rate}%` : "—"} />
            <Card testid="h-payment" label="Paid Orders" value={health.payment_success} />
            <Card testid="h-cancel" label="Cancellation Rate" value={health.cancellation_rate != null ? `${health.cancellation_rate}%` : "—"} />
            <Card testid="h-return" label="Return Rate" value={health.return_rate != null ? `${health.return_rate}%` : "—"} />
            <Card testid="h-approval" label="Approval Queue" value={health.product_approval_queue} />
            <Card testid="h-support" label="Open Tickets" value={health.open_support_tickets} />
          </div>
          <p className="text-xs text-slate-400">{health.note}</p>
        </div>
      )}

      {tab === "sellers" && sellers && (
        <Table testid="admin-seller-table" head={["Seller", "Sales", "Orders", "Fulfilment", "Return Rate", "Rating"]}
          rows={(sellers || []).map((s) => [s.name, inr(s.sales), s.orders, `${s.fulfilment_rate}%`, `${s.return_rate}%`, s.rating ?? "—"])} empty="No seller sales in this period." />
      )}

      {tab === "config" && cfg && (
        <div className="max-w-xl space-y-5" data-testid="config-panel">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="mb-3 text-sm font-semibold text-slate-700">Seller Health Score weights</p>
            {Object.keys(cfg.health_weights).map((k) => (
              <div key={k} className="mb-2 flex items-center justify-between">
                <span className="text-sm capitalize text-slate-600">{k}</span>
                <input type="number" value={cfg.health_weights[k]} onChange={(e) => setCfg({ ...cfg, health_weights: { ...cfg.health_weights, [k]: Number(e.target.value) } })} data-testid={`weight-${k}`} className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="mb-3 text-sm font-semibold text-slate-700">Thresholds</p>
            {Object.keys(cfg.thresholds).map((k) => (
              <div key={k} className="mb-2 flex items-center justify-between">
                <span className="text-sm text-slate-600">{k.replace(/_/g, " ")}</span>
                <input type="number" value={cfg.thresholds[k]} onChange={(e) => setCfg({ ...cfg, thresholds: { ...cfg.thresholds, [k]: Number(e.target.value) } })} data-testid={`threshold-${k}`} className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
              </div>
            ))}
          </div>
          <button onClick={saveCfg} data-testid="save-config" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Save className="h-4 w-4" /> Save & audit</button>
        </div>
      )}
    </div>
  );
}

function Table({ head, rows, empty, testid }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
      {rows.length === 0 ? <p className="p-8 text-center text-sm text-slate-500">{empty}</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{r.map((c, j) => <td key={j} className="px-3 py-2.5 text-slate-700">{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

function MiniList({ title, items, render, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="mb-2 text-sm font-semibold text-slate-700">{title}</p>
      {(!items || items.length === 0) ? <p className="text-xs text-slate-400">Not enough data yet.</p> : (
        <ul className="space-y-1 text-sm text-slate-600">{items.map((x) => <li key={x.category_id}>• {render(x)}</li>)}</ul>
      )}
    </div>
  );
}
